import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import { Navigation } from '../components/Navigation';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { 
  Video, Image, Heart, MessageCircle, Share2, PlusCircle, 
  Trophy, DollarSign, Play, Hash, Send, Pencil, Trash2, MoreVertical, Crown
} from 'lucide-react';
import { toast } from 'sonner';

export default function BundleStories() {
  const { user, loading, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stories, setStories] = useState([]);
  const [loadingStories, setLoadingStories] = useState(true);
  const [hashtag, setHashtag] = useState('');
  const [commentText, setCommentText] = useState({});
  const [showComments, setShowComments] = useState({});
  const [editingStory, setEditingStory] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [showMenu, setShowMenu] = useState({});

  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    }
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) {
      fetchStories();
    }
  }, [user, hashtag]);

  const fetchStories = async () => {
    setLoadingStories(true);
    try {
      const url = hashtag 
        ? `${API}/stories?hashtag=${encodeURIComponent(hashtag)}&limit=20` 
        : `${API}/stories?limit=20`;
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        setStories(await response.json());
      }
    } catch (error) {
      console.error('Error fetching stories:', error);
    } finally {
      setLoadingStories(false);
    }
  };

  const handleLike = async (storyId) => {
    if (!user.is_member) {
      toast.error('Active membership required to like stories. Please upgrade!');
      return;
    }
    try {
      const response = await fetch(`${API}/stories/${storyId}/like`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const result = await response.json();
        setStories(stories.map(s => 
          s.story_id === storyId 
            ? { 
                ...s, 
                likes: result.action === 'liked' 
                  ? [...(s.likes || []), user.user_id]
                  : (s.likes || []).filter(id => id !== user.user_id)
              } 
            : s
        ));
      } else if (response.status === 403) {
        toast.error('Active membership required to like stories. Please upgrade!');
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const handleComment = async (storyId) => {
    const text = commentText[storyId];
    if (!text?.trim()) return;

    if (!user.is_member) {
      toast.error('Active membership required to comment. Please upgrade!');
      return;
    }

    try {
      const response = await fetch(`${API}/stories/${storyId}/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ text })
      });
      if (response.ok) {
        const newComment = await response.json();
        setStories(stories.map(s => 
          s.story_id === storyId 
            ? { ...s, comments: [...(s.comments || []), newComment] }
            : s
        ));
        setCommentText({ ...commentText, [storyId]: '' });
        toast.success('Comment added!');
      } else if (response.status === 403) {
        toast.error('Active membership required to comment. Please upgrade!');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffHours < 48) return 'Yesterday';
    return date.toLocaleDateString();
  };

  const handleDelete = async (storyId) => {
    try {
      const response = await fetch(`${API}/stories/${storyId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        setStories(stories.filter(s => s.story_id !== storyId));
        toast.success('Story deleted');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete story');
      }
    } catch (error) {
      toast.error('Error deleting story');
    }
  };

  const handleEdit = (story) => {
    setEditingStory(story);
    setEditFormData({
      winner_name: story.winner_name,
      amount_won: story.amount_won,
      course_name: story.course_name,
      description: story.description
    });
  };

  const handleSaveEdit = async () => {
    try {
      const response = await fetch(`${API}/stories/${editingStory.story_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editFormData)
      });
      if (response.ok) {
        setStories(stories.map(s => 
          s.story_id === editingStory.story_id 
            ? { ...s, ...editFormData }
            : s
        ));
        setEditingStory(null);
        toast.success('Story updated');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update story');
      }
    } catch (error) {
      toast.error('Error updating story');
    }
  };

  const canEditDelete = (story) => {
    return user && (story.user_id === user.user_id || user.is_admin);
  };

  const groupByDate = (stories) => {
    const groups = {};
    stories.forEach(story => {
      const date = new Date(story.created_at).toLocaleDateString();
      if (!groups[date]) groups[date] = [];
      groups[date].push(story);
    });
    return groups;
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bundle-cream">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-bundle-green"></div>
      </div>
    );
  }

  const groupedStories = groupByDate(stories);

  return (
    <div className="min-h-screen bg-bundle-cream pb-mobile-nav md:pb-8">
      <Navigation user={user} onLogout={logout} />

      <div className="max-w-2xl mx-auto px-4 md:px-8 pt-20 md:pt-24">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            <h1 className="font-heading font-bold text-3xl text-bundle-green mb-1">Bundle Stories</h1>
            <p className="font-body text-gray-600">Share your winning moments</p>
          </div>
          <Button
            data-testid="post-story-btn"
            onClick={() => navigate('/post-story')}
            className="btn-bundle"
          >
            <PlusCircle className="w-4 h-4 mr-2" />
            Post Story
          </Button>
        </div>

        {/* Hashtag Filter */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <button
            onClick={() => setHashtag('')}
            className={`px-4 py-2 rounded-full font-body text-sm whitespace-nowrap transition-colors ${
              !hashtag ? 'bg-bundle-green text-white' : 'bg-white text-bundle-green border border-bundle-cream-dark hover:bg-bundle-cream'
            }`}
          >
            All
          </button>
          {['moneygame', 'birdie', 'eagle', 'thebundle', 'bigwin'].map(tag => (
            <button
              key={tag}
              onClick={() => setHashtag(tag)}
              className={`px-4 py-2 rounded-full font-body text-sm whitespace-nowrap transition-colors ${
                hashtag === tag ? 'bg-bundle-green text-white' : 'bg-white text-bundle-green border border-bundle-cream-dark hover:bg-bundle-cream'
              }`}
            >
              #{tag}
            </button>
          ))}
        </div>

        {/* Stories Feed */}
        {loadingStories ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-bundle-green mx-auto"></div>
          </div>
        ) : stories.length === 0 ? (
          <Card className="bg-white border-bundle-cream-dark">
            <CardContent className="p-12 text-center">
              <Video className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="font-heading text-xl text-bundle-green mb-2">No Stories Yet</h3>
              <p className="font-body text-gray-500 mb-6">Be the first to share a bundle win!</p>
              <Button onClick={() => navigate('/post-story')} className="btn-bundle">
                Post Story
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedStories).map(([date, dateStories]) => (
              <div key={date}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-px flex-1 bg-bundle-cream-dark"></div>
                  <span className="font-body text-sm text-gray-500">{date}</span>
                  <div className="h-px flex-1 bg-bundle-cream-dark"></div>
                </div>

                <div className="space-y-4">
                  {dateStories.map(story => (
                    <Card key={story.story_id} className="bg-white border-bundle-cream-dark overflow-hidden">
                      {/* Header */}
                      <div className="flex items-center gap-3 p-4 border-b border-bundle-cream-dark">
                        {story.user_picture ? (
                          <img src={story.user_picture} alt="" className="w-10 h-10 rounded-full" />
                        ) : (
                          <div className="w-10 h-10 bg-bundle-green rounded-full flex items-center justify-center text-white font-medium">
                            {story.user_name?.charAt(0)}
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-body font-semibold text-bundle-green">{story.user_name}</p>
                          <p className="font-body text-xs text-gray-500">{formatDate(story.created_at)}</p>
                        </div>
                        <div className="text-right flex items-center gap-2">
                          <p className="font-heading text-lg font-bold text-bundle-gold">${story.amount_won}</p>
                          {canEditDelete(story) && (
                            <div className="relative">
                              <button
                                onClick={() => setShowMenu({ ...showMenu, [story.story_id]: !showMenu[story.story_id] })}
                                className="p-1 hover:bg-bundle-cream rounded-full"
                              >
                                <MoreVertical className="w-5 h-5 text-gray-400" />
                              </button>
                              {showMenu[story.story_id] && (
                                <div className="absolute right-0 top-8 bg-white border border-bundle-cream-dark rounded-lg shadow-lg z-10 py-1 min-w-32">
                                  <button
                                    onClick={() => { handleEdit(story); setShowMenu({}); }}
                                    className="w-full px-4 py-2 text-left text-sm font-body text-bundle-green hover:bg-bundle-cream flex items-center gap-2"
                                  >
                                    <Pencil className="w-4 h-4" />
                                    Edit
                                  </button>
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <button className="w-full px-4 py-2 text-left text-sm font-body text-red-600 hover:bg-red-50 flex items-center gap-2">
                                        <Trash2 className="w-4 h-4" />
                                        Delete
                                      </button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Delete Story?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          This action cannot be undone. This will permanently delete this story.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel onClick={() => setShowMenu({})}>Cancel</AlertDialogCancel>
                                        <AlertDialogAction
                                          onClick={() => { handleDelete(story.story_id); setShowMenu({}); }}
                                          className="bg-red-600 hover:bg-red-700"
                                        >
                                          Delete
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Media */}
                      {story.media_url && (
                        <div className="relative aspect-video bg-gray-100">
                          {story.media_type === 'video' ? (
                            <video 
                              src={story.media_url} 
                              className="w-full h-full object-cover" 
                              controls
                            />
                          ) : (
                            <img 
                              src={story.media_url} 
                              alt="" 
                              className="w-full h-full object-cover" 
                            />
                          )}
                        </div>
                      )}

                      {/* Content */}
                      <CardContent className="p-4">
                        {story.is_personal_record && (
                          <div className="flex items-center gap-1.5 mb-2 px-2.5 py-1.5 bg-gradient-to-r from-bundle-gold/10 to-amber-100 rounded-lg w-fit" data-testid="pr-badge">
                            <Crown className="w-4 h-4 text-bundle-gold" />
                            <span className="font-heading text-xs font-bold text-bundle-gold-dark uppercase tracking-wide">Personal Record</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 mb-3">
                          <Trophy className="w-5 h-5 text-bundle-gold" />
                          <span className="font-body font-semibold text-bundle-green">
                            {story.winner_name} won the bundle!
                          </span>
                        </div>
                        <p className="font-body text-gray-700 mb-2">{story.description}</p>
                        <p className="font-body text-sm text-gray-500 mb-3">
                          At {story.course_name}
                          {story.players?.length > 0 && ` • With ${story.players.join(', ')}`}
                        </p>

                        {/* Hashtags */}
                        {story.hashtags?.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-4">
                            {story.hashtags.map(tag => (
                              <button
                                key={tag}
                                onClick={() => setHashtag(tag)}
                                className="text-bundle-green hover:text-bundle-gold text-sm font-body"
                              >
                                #{tag}
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center gap-6 pt-3 border-t border-bundle-cream-dark">
                          <button
                            onClick={() => handleLike(story.story_id)}
                            className={`flex items-center gap-2 transition-colors ${
                              story.likes?.includes(user.user_id) 
                                ? 'text-red-500' 
                                : 'text-gray-500 hover:text-red-500'
                            }`}
                          >
                            <Heart className={`w-5 h-5 ${story.likes?.includes(user.user_id) ? 'fill-current' : ''}`} />
                            <span className="font-body text-sm">{story.likes?.length || 0}</span>
                          </button>
                          <button
                            onClick={() => setShowComments({ ...showComments, [story.story_id]: !showComments[story.story_id] })}
                            className="flex items-center gap-2 text-gray-500 hover:text-bundle-green transition-colors"
                          >
                            <MessageCircle className="w-5 h-5" />
                            <span className="font-body text-sm">{story.comments?.length || 0}</span>
                          </button>
                          <button className="flex items-center gap-2 text-gray-500 hover:text-bundle-green transition-colors">
                            <Share2 className="w-5 h-5" />
                          </button>
                        </div>

                        {/* Comments Section */}
                        {showComments[story.story_id] && (
                          <div className="mt-4 pt-4 border-t border-bundle-cream-dark">
                            {story.comments?.length > 0 && (
                              <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
                                {story.comments.map(comment => (
                                  <div key={comment.comment_id} className="flex gap-2">
                                    {comment.user_picture ? (
                                      <img src={comment.user_picture} alt="" className="w-8 h-8 rounded-full" />
                                    ) : (
                                      <div className="w-8 h-8 bg-bundle-green rounded-full flex items-center justify-center text-white text-xs">
                                        {comment.user_name?.charAt(0)}
                                      </div>
                                    )}
                                    <div className="flex-1 bg-bundle-cream/50 rounded-lg px-3 py-2">
                                      <p className="font-body text-sm font-semibold text-bundle-green">{comment.user_name}</p>
                                      <p className="font-body text-sm text-gray-700">{comment.text}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="flex gap-2">
                              <Input
                                placeholder="Add a comment..."
                                value={commentText[story.story_id] || ''}
                                onChange={(e) => setCommentText({ ...commentText, [story.story_id]: e.target.value })}
                                onKeyPress={(e) => e.key === 'Enter' && handleComment(story.story_id)}
                                className="flex-1 border-bundle-cream-dark"
                              />
                              <Button
                                onClick={() => handleComment(story.story_id)}
                                size="icon"
                                className="btn-bundle"
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Story Dialog */}
      <Dialog open={!!editingStory} onOpenChange={() => setEditingStory(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-bundle-green">Edit Story</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="font-body text-bundle-green">Winner Name</Label>
              <Input
                value={editFormData.winner_name || ''}
                onChange={(e) => setEditFormData({ ...editFormData, winner_name: e.target.value })}
                className="border-bundle-cream-dark"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-body text-bundle-green">Amount Won ($)</Label>
              <Input
                type="number"
                value={editFormData.amount_won || ''}
                onChange={(e) => setEditFormData({ ...editFormData, amount_won: parseFloat(e.target.value) })}
                className="border-bundle-cream-dark"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-body text-bundle-green">Course Name</Label>
              <Input
                value={editFormData.course_name || ''}
                onChange={(e) => setEditFormData({ ...editFormData, course_name: e.target.value })}
                className="border-bundle-cream-dark"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-body text-bundle-green">Description</Label>
              <textarea
                value={editFormData.description || ''}
                onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-bundle-cream-dark rounded-md font-body resize-none focus:border-bundle-green focus:outline-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingStory(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} className="btn-bundle">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
